import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Swal from 'sweetalert2'
import CertificationsService from '@/services/CertificationsService'
import ProjectsService from '@/services/ProjectsService'

const toDateInputValue = value => {
	if (!value) return ''
	return `${value}`.substring(0, 10)
}

const addOneDay = dateValue => {
	const parsed = moment(toDateInputValue(dateValue), 'YYYY-MM-DD', true)
	if (!parsed.isValid()) {
		return moment().add(1, 'day').format('YYYY-MM-DD')
	}
	return parsed.add(1, 'day').format('YYYY-MM-DD')
}

const withStageNames = steps => (
	(Array.isArray(steps) ? steps : []).map((step, index) => ({
		...step,
		nazwa: `Etap ${index + 1}`,
	}))
)

const buildNextDayStepTemplate = (source = {}) => {
	const nextDate = addOneDay(source.data_rozpoczecia || source.data_zakonczenia)
	const adres = `${source.adres ?? source.ulica ?? ''}`.trim()

	return {
		nazwa: '',
		status: 'new',
		placowkaId: source.placowkaId || null,
		placowkaName: `${source.placowkaName || ''}`.trim(),
		placowkaAddress: `${source.placowkaAddress || ''}`.trim(),
		adres,
		ulica: adres,
		miasto: `${source.miasto || ''}`.trim(),
		kod_pocztowy: `${source.kod_pocztowy || ''}`.trim(),
		g_placeid: source.g_placeid || '',
		g_lat: source.g_lat ?? '',
		g_long: source.g_long ?? '',
		osoba_kontaktowa: {
			nazwisko: `${source.osoba_kontaktowa?.nazwisko ?? ''}`.trim(),
			funkcja: `${source.osoba_kontaktowa?.funkcja ?? ''}`.trim(),
			telefon: `${source.osoba_kontaktowa?.telefon ?? ''}`.trim(),
		},
		data_rozpoczecia: nextDate,
		data_zakonczenia: nextDate,
		startTime: '',
		endTime: '',
		pozycje: [],
		notatki: '',
		certificates: [],
	}
}

export default function useProjectDetailsPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()
	const [project, setProject] = useState(null)
	const [loading, setLoading] = useState(true)
	const [savingCommon, setSavingCommon] = useState(false)
	const [savingStep, setSavingStep] = useState(false)
	const [stepEditorOpen, setStepEditorOpen] = useState(false)
	const [stepEditorKey, setStepEditorKey] = useState(0)
	const [editingStep, setEditingStep] = useState(null)
	const [editingStepIndex, setEditingStepIndex] = useState(null)
	const [previousStepForEditor, setPreviousStepForEditor] = useState(null)
	const [certificationOptions, setCertificationOptions] = useState([])
	const [contentOptions, setContentOptions] = useState([])

	const refreshProject = useCallback(async () => {
		const result = await ProjectsService.getById(id)
		if (!result.error && result.data) {
			setProject(result.data)
		}
		return result
	}, [id])

	const loadPage = useCallback(async () => {
		setLoading(true)

		const [projectResult, certificationsResult, contentResult] = await Promise.all([
			ProjectsService.getById(id),
			CertificationsService.getAll({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
			ProjectsService.getContentOptions(),
		])

		if (projectResult.error || !projectResult.data) {
			await Swal.fire(
				t('projects.details.messages.errorTitle', { defaultValue: t('notifications.errorTitle') }),
				projectResult.error || t('projects.details.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/projekty')
			return
		}

		setProject(projectResult.data)
		setCertificationOptions(
			(Array.isArray(certificationsResult.data) ? certificationsResult.data : []).map(item => ({
				value: `${item._id || item.id || ''}`.trim(),
				label: item.name || '—',
			})).filter(item => item.value)
		)
		setContentOptions(Array.isArray(contentResult.data) ? contentResult.data : [])
		setLoading(false)
	}, [id, navigate, t])

	useEffect(() => {
		if (id) loadPage()
	}, [id, loadPage])

	const createdDate = useMemo(() => {
		if (!project?.created) return '—'
		return `${project.created}`.substring(0, 10)
	}, [project?.created])

	const trainingNameOptions = useMemo(() => {
		const names = new Set()
		;(Array.isArray(project?.steps) ? project.steps : []).forEach(step => {
			if (step?.nazwa) names.add(step.nazwa)
		})
		if (project?.nazwa) names.add(project.nazwa)
		return [...names].map(name => ({ value: name, label: name }))
	}, [project])

	const buildProjectUpdatePayload = useCallback((overrides = {}) => ({
		nazwa: project?.nazwa || '',
		status: project?.status || 'new',
		opis: project?.opis || '',
		uwagi: project?.uwagi || '',
		steps: Array.isArray(project?.steps) ? project.steps : [],
		...overrides,
	}), [project])

	const saveCommonFields = useCallback(async ({ opis }) => {
		if (!project) return
		setSavingCommon(true)
		const result = await ProjectsService.update(id, buildProjectUpdatePayload({ opis }))
		setSavingCommon(false)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('projects.edit.messages.updateFailed'),
				'error'
			)
			return
		}

		setProject(result.data)
		await Swal.fire(
			t('notifications.successTitle'),
			t('projects.details.messages.commonSaved', { defaultValue: 'Zapisano opis' }),
			'success'
		)
	}, [buildProjectUpdatePayload, id, project, t])

	const openAddStep = useCallback(() => {
		const steps = Array.isArray(project?.steps) ? project.steps : []
		setPreviousStepForEditor(steps.length ? steps[steps.length - 1] : null)
		setEditingStep(null)
		setEditingStepIndex(null)
		setStepEditorKey(prev => prev + 1)
		setStepEditorOpen(true)
	}, [project?.steps])

	const openEditStep = useCallback((step, index) => {
		const steps = Array.isArray(project?.steps) ? project.steps : []
		setPreviousStepForEditor(index > 0 ? steps[index - 1] : null)
		setEditingStep(step)
		setEditingStepIndex(index)
		setStepEditorKey(prev => prev + 1)
		setStepEditorOpen(true)
	}, [project?.steps])

	const openAddNextDayStep = useCallback(sourceStep => {
		setPreviousStepForEditor(sourceStep || null)
		setEditingStep(buildNextDayStepTemplate(sourceStep))
		setEditingStepIndex(null)
		setStepEditorKey(prev => prev + 1)
		setStepEditorOpen(true)
	}, [])

	const closeStepEditor = useCallback(() => {
		setStepEditorOpen(false)
		setEditingStep(null)
		setEditingStepIndex(null)
		setPreviousStepForEditor(null)
	}, [])

	const saveStep = useCallback(async (stepPayload, options = {}) => {
		if (!project) return false
		setSavingStep(true)

		const currentSteps = Array.isArray(project.steps) ? [...project.steps] : []
		let nextSteps
		if (editingStepIndex != null && editingStepIndex >= 0) {
			nextSteps = currentSteps.map((item, index) => (
				index === editingStepIndex ? { ...item, ...stepPayload } : item
			))
		} else {
			nextSteps = [...currentSteps, stepPayload]
		}

		const result = await ProjectsService.update(id, buildProjectUpdatePayload({ steps: withStageNames(nextSteps) }))
		setSavingStep(false)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('projects.edit.messages.updateFailed'),
				'error'
			)
			return false
		}

		setProject(result.data)

		if (options.openNextDay) {
			openAddNextDayStep({
				...stepPayload,
				placowkaAddress: options.placowkaAddress || stepPayload.placowkaAddress || '',
			})
			return true
		}

		closeStepEditor()
		return true
	}, [buildProjectUpdatePayload, closeStepEditor, editingStepIndex, id, openAddNextDayStep, project, t])

	const removeStep = useCallback(async (step, index) => {
		const confirmation = await Swal.fire({
			title: t('projects.details.steps.deleteTitle', { defaultValue: 'Usunąć etap?' }),
			text: t('projects.details.steps.deleteText', {
				defaultValue: 'Usunąć etap „{{name}}”?',
				name: t('projects.details.steps.stepLabel', {
					defaultValue: 'Etap {{number}}',
					number: index + 1,
				}),
			}),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('projects.list.deleteDialog.confirmButton'),
			cancelButtonText: t('projects.list.deleteDialog.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!confirmation.isConfirmed || !project) return

		const nextSteps = (Array.isArray(project.steps) ? project.steps : [])
			.filter((_, stepIndex) => stepIndex !== index)

		const result = await ProjectsService.update(id, buildProjectUpdatePayload({ steps: withStageNames(nextSteps) }))
		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('projects.edit.messages.updateFailed'),
				'error'
			)
			return
		}

		setProject(result.data)
	}, [buildProjectUpdatePayload, id, project, t])

	const uploadProjectFile = useCallback(async (file, onUploadProgress) => {
		const result = await ProjectsService.uploadProjectFile(id, file, onUploadProgress)
		if (result.success) {
			await refreshProject()
		}
		return result
	}, [id, refreshProject])

	const downloadProjectFile = useCallback(
		fileUuid => ProjectsService.downloadProjectFile(id, fileUuid),
		[id]
	)

	const deleteProjectFile = useCallback(async fileUuid => {
		const result = await ProjectsService.deleteProjectFile(id, fileUuid)
		if (result.success) {
			await refreshProject()
		}
		return result
	}, [id, refreshProject])

	return {
		project,
		loading,
		createdDate,
		savingCommon,
		savingStep,
		stepEditorOpen,
		stepEditorKey,
		editingStep,
		previousStepForEditor,
		certificationOptions,
		trainingNameOptions,
		contentOptions,
		saveCommonFields,
		openAddStep,
		openEditStep,
		openAddNextDayStep,
		closeStepEditor,
		saveStep,
		removeStep,
		uploadProjectFile,
		downloadProjectFile,
		deleteProjectFile,
	}
}
